// Import hooks.
import {
	useInputForm,
	UseInputFormResult,
} from '@app/hooks/view/input';
// Import app models.
import {
	ValidatorResponse,
} from '@app/models/app';
// Import props.
import {
	InputFormProps,
	InputGoogleMapProps,
} from '@app/props';
// Import enumerations.
import {
	GpsAccuracy
} from '@app/enumerations';

interface UseInputGoogleMapState {
	lat?: number;
	lng?: number;
	hasMovedLocation: boolean;
}

interface UseInputGoogleMapParameters {
	id: string;
	label: string;
	required: boolean;
	disabled: boolean;
	initialInput?: UseInputGoogleMapState;
	showValidation?: boolean;
	currentGpsAccuracy?: GpsAccuracy;
}

interface UseInputGoogleMapResults extends UseInputFormResult<UseInputGoogleMapState, InputGoogleMapProps> {
	setLocation: (lat: number, lng: number) => void;
}

const useInputGoogleMap = (params: UseInputGoogleMapParameters): UseInputGoogleMapResults => {

	// #region exported methods

	/**
	 * Event handler for processing coordinate changes.
	 * @param address
	 */
	const setLocation = (lat: number | undefined, lng: number | undefined): void => {
		other.setInput({
			lat: lat,
			lng: lng,
			hasMovedLocation: true,
		});
	}

	/**
	 * Returns props for form input. 
	 **/
	const applyGoogleMapProps = (): InputGoogleMapProps => {
		// First apply the form props.
		let formProps: InputFormProps = applyProps();
		// Then apply the google map props.
		return {
			...formProps,
			lat: other.value.lat,
			lng: other.value.lng,
			onChange: setLocation,
		}
	};

	// #endregion exported methods


	// #region private methods

	/**
	 * Validates the address.
	 * @param state
	 * @param required
	 */
	const validateGoogleMap = (state: UseInputGoogleMapState, required: boolean): ValidatorResponse => {
		let validatorResponse: ValidatorResponse = new ValidatorResponse(true, String.empty());
		if (required
			&& params.currentGpsAccuracy !== undefined
			&& params.currentGpsAccuracy !== GpsAccuracy.ROOFTOP
			&& params.currentGpsAccuracy !== GpsAccuracy.PRODUCER_SELECTED
			&& params.currentGpsAccuracy !== GpsAccuracy.UNDERWRITER_SELECTED
			&& !state.hasMovedLocation) {
			validatorResponse = new ValidatorResponse(false, 'The address coordinates are not correct, please move the marker to the correct location on the map.');
		}
		else if (state.lat === undefined
			|| state.lng === undefined) {
			validatorResponse = new ValidatorResponse(false, 'The address coordinates are invalid, please provide a valid location.');
		}
		else if (state.lat > 85 || state.lat < -85
			|| state.lng > 180 || state.lng < -180) {
			validatorResponse = new ValidatorResponse(false, 'The address coordinates are out of bounds.  Please make sure the latitude value is between 85 and -85, and the longitude value is between 180 and -180.');
		}

		return validatorResponse;
	}

	// #endregion private methods


	// #region variables

	// Instantiate the 'address' input.
	const { applyProps, ...other } = useInputForm<UseInputGoogleMapState>({
		id: params.id,
		label: params.label,
		required: params.required,
		disabled: params.disabled,
		initialInput: params.initialInput ?? { lat: 0, lng: 0, hasMovedLocation: false },
		showValidation: params.showValidation,
		validators: [validateGoogleMap],
	});

	// #endregion variables


	return {
		applyProps: applyGoogleMapProps,
		setLocation: setLocation,
		...other,
	};
}

// Export parameters.
export {
	UseInputGoogleMapParameters,
};

// Export results.
export {
	UseInputGoogleMapResults,
};

// Export state.
export {
	UseInputGoogleMapState,
};

export default useInputGoogleMap;